import { useCallback, useState } from "react";
import { StyleSheet, View } from "react-native";
import { Form, Formik, FormikHelpers } from "formik";

import {
  ActivityIndicator,
  Banner,
  Button,
  DrawerActions,
  DrawerContent,
  DrawerHeader,
  FormikSubmit,
  FormikSwitch,
  FormSection,
  Result,
  Typography,
  useTheme,
  VStack,
} from "@smartrent/ui";

import { useDrawerNav } from "@/common/AppDrawer";
import { CopyText } from "@/common/CopyText";
import { OrganizationProps } from "@/types";
import { formatDate } from "@/utils/date";
import {
  SsoSetupCreateResponse,
  useSsoSetupMutation,
  useSsoSetupQuery,
} from "@/api/sso-setup";

// The API collapses its own guards and CMW's error bodies into a single `{ error }`
// message written for an operator to read, so prefer it over this fallback. The
// fallback deliberately says no link was created: an ambiguous failure is the one
// state this feature exists to prevent.
const ERROR_FALLBACK =
  "Something went wrong generating the setup link. No link was created — please try again.";

interface SsoSetupProps {
  organization: OrganizationProps;
}

interface SsoSetupFormValues {
  include_scim: boolean;
}

export function SsoSetup({ organization }: SsoSetupProps) {
  const drawer = useDrawerNav();
  const { colors } = useTheme();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // The minted link lives here and nowhere else — not in the query cache, not in
  // the database. Closing the drawer unmounts this and the link is gone, which is
  // the same guarantee Auth0 gives: shown once. Generating another is the recovery
  // path, so there is nothing to lose by letting it go.
  const [generated, setGenerated] = useState<SsoSetupCreateResponse | null>(
    null
  );

  const { data, isLoading, isError } = useSsoSetupQuery({
    organizationId: organization.id,
  });

  const [generateLink] = useSsoSetupMutation();

  const ticket = data?.ticket ?? null;

  // A connection name outranks a sent link: it is only captured once the client has
  // finished Auth0's wizard, so CMW's webhook is the source of truth even when a
  // setup link has gone out and not been used.
  const connectionName = data?.auth0_connection_name?.trim() || null;

  const handleSubmit = useCallback(
    async (
      values: SsoSetupFormValues,
      actions: FormikHelpers<SsoSetupFormValues>
    ) => {
      setErrorMessage(null);

      try {
        const response = await generateLink({
          organizationId: organization.id,
          include_scim: values.include_scim,
        });

        setGenerated(response);
      } catch (err) {
        setErrorMessage(
          (err as any)?.response?.data?.error?.trim() || ERROR_FALLBACK
        );
      } finally {
        actions.setSubmitting(false);
      }
    },
    [generateLink, organization.id]
  );

  // Checked before the query's own loading and error states, and reading nothing
  // from `data`: this is the only chance to copy the link, so the background
  // refetch that follows generation must not be able to take the screen. A failed
  // refetch flips the query to `error` even though it still holds data, which
  // would otherwise swap a live link for an error panel and lose it for good.
  if (generated) {
    return (
      <>
        <DrawerHeader title="SSO Setup" />
        <DrawerContent>
          <View style={styles.content}>
            <Banner color="warning">
              Copy this link now and send it to the client. It is shown once and
              is not stored anywhere — closing this drawer loses it. If that
              happens, generate a new one; the client just uses the newer link.
            </Banner>

            <VStack spacing={8}>
              <Typography type="title5" color="textSecondary">
                Self-Service Setup Link
              </Typography>
              <CopyText text={generated.url}>
                <View
                  style={[styles.urlBlock, { borderColor: colors.gray200 }]}
                >
                  <Typography selectable style={styles.urlText}>
                    {generated.url}
                  </Typography>
                </View>
              </CopyText>
            </VStack>

            <VStack spacing={4}>
              <Typography type="caption" color="textSecondary">
                {`SCIM provisioning: ${
                  generated.ticket?.include_scim ? "included" : "not included"
                }`}
              </Typography>
              <Typography type="caption" color="textSecondary">
                Auth0 decides how long this link stays valid. If the client
                cannot use it, generate a new one.
              </Typography>
            </VStack>
          </View>
        </DrawerContent>
        <DrawerActions>
          <Button onPress={drawer.pop} variation="plain">
            Close
          </Button>
        </DrawerActions>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <DrawerHeader title="SSO Setup" />
        <DrawerContent>
          <View style={styles.content}>
            <ActivityIndicator />
          </View>
        </DrawerContent>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <DrawerHeader title="SSO Setup" />
        <DrawerContent>
          <View style={styles.content}>
            <Result
              title="Error"
              description="Failed to load SSO setup status."
              status="error"
              graphic
            />
          </View>
        </DrawerContent>
        <DrawerActions>
          <Button onPress={drawer.pop} variation="plain">
            Close
          </Button>
        </DrawerActions>
      </>
    );
  }

  if (connectionName) {
    return (
      <>
        <DrawerHeader title="SSO Setup" />
        <DrawerContent>
          <View style={styles.content}>
            <VStack spacing={4}>
              <Typography type="title5" color="textSecondary">
                Auth0 Connection
              </Typography>
              <Typography>{connectionName}</Typography>
              <Typography color="textSecondary" style={styles.spacedNote}>
                SSO is already configured for this organization, so a new setup
                link cannot be generated.
              </Typography>
            </VStack>
          </View>
        </DrawerContent>
        <DrawerActions>
          <Button onPress={drawer.pop} variation="plain">
            Close
          </Button>
        </DrawerActions>
      </>
    );
  }

  // An org with a link already out and one that has never had one share this form.
  // The first only differs by what it has to explain: a link went out, and this
  // drawer cannot show it again. There is no confirmation step, because generating
  // a replacement destroys nothing.
  //
  // Nothing here reasons about whether the earlier link still works. Auth0 owns
  // that, we cannot observe the client opening it, and the SA's next move is the
  // same either way — they are here because the client needs a link.
  const priorLinkMessage = ticket
    ? [
        `A setup link was generated by ${
          ticket.created_by ?? "an unknown account"
        } on ${formatDate({ date: ticket.created_at })}.`,
        "It is not stored and cannot be shown again — generate a new one below if the client needs it.",
        "Auth0 decides when a link expires, and a new link does not disable an older one.",
      ].join(" ")
    : null;

  const initialValues: SsoSetupFormValues = {
    // Carried over so a replacement link opens the same wizard the client was
    // already told to expect. SCIM defaults to No for a first-time link.
    include_scim: ticket?.include_scim ?? false,
  };

  return (
    <>
      <DrawerHeader title="SSO Setup" />
      <Formik initialValues={initialValues} onSubmit={handleSubmit}>
        {() => (
          <>
            <DrawerContent>
              <View style={styles.content}>
                {priorLinkMessage ? (
                  <Banner color="info">{priorLinkMessage}</Banner>
                ) : null}

                {errorMessage ? (
                  <Banner color="error">{errorMessage}</Banner>
                ) : null}

                <Typography color="textSecondary">
                  {`Generates an Auth0 setup link for ${organization.name} to hand to the client's IT admin. The link is shown here once and is not saved — generate another any time.`}
                </Typography>

                <Form>
                  <FormSection
                    title="Wizard Options"
                    style={styles.firstSection}
                  >
                    <FormikSwitch
                      name="include_scim"
                      label="Include SCIM provisioning"
                      assistiveText="When enabled, the Auth0 wizard prompts the client to generate a SCIM bearer token and configure user provisioning in their IDP."
                    />
                  </FormSection>
                </Form>
              </View>
            </DrawerContent>
            <DrawerActions>
              <Button onPress={drawer.pop} variation="plain">
                Close
              </Button>
              <FormikSubmit
                label={ticket ? "Generate New Link" : "Generate Link"}
                submittingLabel="Generating..."
              />
            </DrawerActions>
          </>
        )}
      </Formik>
    </>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: 16,
    gap: 16,
  },
  firstSection: {
    borderTopWidth: 0,
    paddingBottom: 16,
  },
  spacedNote: {
    marginTop: 8,
  },
  urlBlock: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 4,
    padding: 8,
    marginRight: 8,
  },
  urlText: {
    fontFamily: "monospace",
    fontSize: 12,
  },
});
